import React from "react";
import { Check, CheckCircle2, TrendingUp, Star } from "lucide-react";
import { cn } from "../../../../lib/utils";

export const PlanCard = ({
  plan,
  selectedPlan,
  onSelectPlan,
  hasActivePlan = false,
  activePlanId = null,
}) => {
  const isActive = hasActivePlan && activePlanId === plan.id;
  const isSelected = selectedPlan === plan.id;
  const isClickable = !hasActivePlan || activePlanId !== plan.id;
  const planPrice = plan.price || {};
  const isGrowth = plan.name?.toLowerCase().includes("growth");
  const isPopular = plan.isPopular;

  return (
    <button
      type="button"
      disabled={!isClickable}
      onClick={() => isClickable && onSelectPlan(plan.id)}
      className={cn(
        "text-left bg-white dark:bg-slate-900 rounded-xl p-5 transition-all relative flex flex-col h-full border-2",
        isSelected
          ? "border-[hsl(158,48%,36%)] ring-2 ring-[hsl(158,48%,36%)]/25 shadow-sm"
          : isGrowth
            ? "border-[#fbbf24] hover:border-amber-500 shadow-sm"
            : "border-purple-200 dark:border-purple-800/60 hover:border-purple-400",
        !isClickable && "cursor-not-allowed opacity-80"
      )}
    >
      {isPopular && (
        <div
          className={cn(
            "absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-sm uppercase tracking-wider",
            isSelected
              ? "bg-[hsl(158,48%,36%)] text-white"
              : isGrowth
                ? "bg-[#fbbf24] text-[#78350f]"
                : "bg-purple-600 text-white"
          )}
        >
          <Star className="w-3 h-3 fill-current" />
          Best Seller
        </div>
      )}

      <div className={cn("mb-4", isPopular && "mt-1")}>
        <div className="flex items-center gap-2.5 mb-2">
          <div
            className={cn(
              "p-1.5 rounded-lg shrink-0",
              isSelected
                ? "bg-[hsl(158,48%,36%)]/15 text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,55%)]"
                : isGrowth
                  ? "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400"
                  : "bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400"
            )}
          >
            {isGrowth ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <CheckCircle2 className="w-4 h-4 fill-current" />
            )}
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {plan.name}
          </h3>
        </div>

        {plan.badgeText && (
          <span
            className={cn(
              "inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-widest mb-2",
              isSelected
                ? "bg-[hsl(158,48%,36%)]/15 text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,55%)]"
                : isGrowth
                  ? "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
                  : "bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-300"
            )}
          >
            {plan.badgeText}
          </span>
        )}

        {plan.tagline && (
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
            {plan.tagline}
          </p>
        )}
      </div>

      <div className="mb-4">
        <p className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
          {planPrice.displayPriceTitle}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {planPrice.displayPriceSubtitle}
        </p>
        {planPrice.savingsAmount > 0 && (
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <p className="text-xs font-medium text-green-600 dark:text-green-400">
              You save ₹{planPrice.savingsAmount.toLocaleString()} vs monthly
            </p>
            {isGrowth && planPrice.dailyCost > 0 && (
              <span className="bg-green-100 dark:bg-green-950/50 text-green-800 dark:text-green-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                ₹{planPrice.dailyCost}/day
              </span>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 dark:border-slate-800 pt-4 flex-1">
        {plan.featuresHeader && (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">
            {plan.featuresHeader}
          </p>
        )}
        <ul className="space-y-2">
          {(plan.displayFeatures || []).map((feature, idx) => {
            if (feature.type === "divider") {
              return (
                <li
                  key={idx}
                  className="pt-2 pb-1 text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide"
                >
                  {feature.text}
                </li>
              );
            }
            return (
              <li
                key={idx}
                className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-300"
              >
                <Check
                  className={cn(
                    "w-4 h-4 mt-0.5 shrink-0",
                    isSelected
                      ? "text-[hsl(158,45%,55%)]"
                      : isGrowth
                        ? "text-amber-500"
                        : "text-purple-500"
                  )}
                />
                <span>{feature.text}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <div
        className={cn(
          "mt-5 pt-3 border-t border-gray-100 dark:border-slate-800 text-center text-xs font-semibold",
          isSelected
            ? "text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,55%)]"
            : "text-gray-400 dark:text-gray-500"
        )}
      >
        {isActive ? "Current plan" : isSelected ? "Selected" : "Click to select"}
      </div>
    </button>
  );
};
